import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { requestTypes } from '../../data/site';
type FormValues = {
  name: string;
  phone: string;
  email: string;
  town: string;
  type: string;
  message: string;
};
type FormErrors = Partial<Record<keyof FormValues, string>>;
type Status = 'idle' | 'submitting' | 'success' | 'error';
const initialValues: FormValues = {
  name: '',
  phone: '',
  email: '',
  town: '',
  type: requestTypes[0],
  message: ''
};
export function QuoteForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<Status>('idle');
  const update = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues(v => ({
      ...v,
      [field]: e.target.value
    }));
    if (errors[field]) setErrors(er => ({
      ...er,
      [field]: undefined
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setStatus('submitting');
    try {
      await new Promise(resolve => setTimeout(resolve, 900));
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  const reset = () => {
    setValues(initialValues);
    setStatus('idle');
  };
  return <div className="rounded-xl bg-cream p-6 md:p-10" data-id="element-144" data-mp="1kymcmg6nedle">
      <AnimatePresence mode="wait" initial={false} data-id="element-145" data-mp="1kze69lgoq191">
        {status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} role="status" className="py-10" data-id="element-146" data-mp="1pyqry2vtl2a1">
            <CheckCircle2Icon className="h-10 w-10 text-moss" aria-hidden="true" data-id="element-147" data-mp="36cqt1h6eixyv" />
            <h3 className="mt-5 font-serif text-3xl text-forest" data-id="element-148" data-mp="16ar89ewosykk">Merci, {values.name.split(' ')[0]} !</h3>
            <p className="mt-3 max-w-md leading-relaxed text-ink-muted" data-id="element-149" data-mp="11bo56009p2f8">
              Votre demande est bien envoyée. Je vous recontacte rapidement pour organiser une visite.
            </p>
            <button type="button" onClick={reset} className="mt-8 rounded-full border border-forest px-5 py-2.5 text-sm font-medium text-forest transition-colors duration-150 hover:bg-forest hover:text-cream" data-id="element-150" data-mp="1b5n1gidntrz9">
              Envoyer une autre demande
            </button>
          </motion.div> : <motion.form key="form" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onSubmit={handleSubmit} noValidate className="grid gap-6 md:grid-cols-2" data-id="element-151" data-mp="ripbw4ttsnpa">
            <Field id="name" label="Nom et prénom" error={errors.name} data-id="element-152" data-mp="3lsgwjw2ujno4">
              <input id="name" autoComplete="name" value={values.name} onChange={update('name')} className={inputClass(errors.name)} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} data-id="element-153" data-mp="3ql0bawd9soui" />
            </Field>
            <Field id="town" label="Commune" error={errors.town} data-id="element-154" data-mp="3gsuhri7ddzd7">
              <input id="town" autoComplete="address-level2" value={values.town} onChange={update('town')} className={inputClass(errors.town)} aria-invalid={!!errors.town} aria-describedby={errors.town ? 'town-error' : undefined} data-id="element-155" data-mp="8g6nmaua9bo6" />
            </Field>
            <Field id="phone" label="Téléphone" error={errors.phone} data-id="element-156" data-mp="3g28ao542mzj">
              <input id="phone" type="tel" autoComplete="tel" value={values.phone} onChange={update('phone')} className={inputClass(errors.phone)} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'phone-error' : undefined} data-id="element-157" data-mp="3e7ml7m0mque" />
            </Field>
            <Field id="email" label="E-mail" error={errors.email} data-id="element-158" data-mp="34do1bh67fmud">
              <input id="email" type="email" autoComplete="email" value={values.email} onChange={update('email')} className={inputClass(errors.email)} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} data-id="element-159" data-mp="2zmpioaxqucny" />
            </Field>
            <Field id="type" label="Type de demande" className="md:col-span-2" data-id="element-160" data-mp="2pq2ylod8rpmz">
              <select id="type" value={values.type} onChange={update('type')} className={inputClass()} data-id="element-161" data-mp="3nb8s4tsj4vhm">
                {requestTypes.map(t => <option key={t} data-id="element-162" data-mp="3ibcoivnqv62l">{t}</option>)}
              </select>
            </Field>
            <Field id="message" label="Votre jardin et vos besoins" error={errors.message} className="md:col-span-2" data-id="element-163" data-mp="38f18pvoy3y3e">
              <textarea id="message" rows={5} value={values.message} onChange={update('message')} placeholder="Surface approximative, travaux souhaités, fréquence…" className={inputClass(errors.message)} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'message-error' : undefined} data-id="element-164" data-mp="21zgrbzb2a9e1" />
            </Field>

            {status === 'error' && <p role="alert" className="text-sm text-clay md:col-span-2" data-id="element-165" data-mp="hetui5qucv53">
                L’envoi a échoué. Merci de réessayer dans un instant.
              </p>}

            <div className="flex flex-col gap-4 md:col-span-2 md:flex-row md:items-center md:justify-between" data-id="element-166" data-mp="r8anj7j5m67p">
              <p className="text-sm text-ink-muted" data-id="element-167" data-mp="w5j81errymc4">Vos informations servent uniquement à répondre à votre demande.</p>
              <button type="submit" disabled={status === 'submitting'} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-forest px-7 py-3.5 text-sm font-medium text-cream transition-colors duration-150 hover:bg-forest-dark disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2" data-id="element-168" data-mp="1098v1zsswvv1">
                {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-169" data-mp="33wr8uinyfk23" />}
                {status === 'submitting' ? 'Envoi en cours…' : 'Envoyer ma demande'}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>
    </div>;
}
type FieldProps = {
  id: string;
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
};
function Field({
  id,
  label,
  error,
  className = '',
  children
}: FieldProps) {
  return <div className={className} data-id="element-170" data-mp="3isqd1i7y101y">
      <label htmlFor={id} className="block text-sm font-medium text-ink" data-id="element-171" data-mp="3irp3ji6kslmc">
        {label}
      </label>
      <div className="mt-2" data-id="element-172" data-mp="hd8t497bw78">{children}</div>
      {error && <p id={`${id}-error`} className="mt-1.5 text-sm text-clay" data-id="element-173" data-mp="3rmdadcrsubd9">
          {error}
        </p>}
    </div>;
}
function inputClass(error?: string) {
  return `w-full rounded-lg border bg-white px-4 py-3 text-ink placeholder:text-ink-muted/60 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-forest/30 ${error ? 'border-clay focus:border-clay' : 'border-line focus:border-forest'}`;
}
function validate(values: FormValues): FormErrors {
  const errors: FormErrors = {};
  if (!values.name.trim()) errors.name = 'Indiquez votre nom.';
  if (!values.town.trim()) errors.town = 'Indiquez votre commune.';
  if (!values.phone.trim() && !values.email.trim()) {
    errors.phone = 'Indiquez un téléphone ou un e-mail.';
  }
  if (values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = 'Adresse e-mail invalide.';
  }
  if (values.phone.trim() && values.phone.replace(/\D/g, '').length < 10) {
    errors.phone = 'Numéro de téléphone invalide.';
  }
  if (!values.message.trim()) errors.message = 'Décrivez brièvement votre besoin.';
  return errors;
}